import React, { useCallback, useEffect, useRef, useState } from "react";
import "./OngoingCharges.css";
import AddChargeComponent from "./AddChargeComponent";
import axios from "axios";
import ENDPOINTS, { API_BASE_URL } from "../../../Constants/ApiEndpoints";
import { calculateDuration, dateTimeFormatter, dateTimeFormatter2 } from "../../../CommonComponents/Utils/DateTimeFormatter";
import OngoingChargesReceipt from "../../../CommonComponents/Invoice Receipts/OngoingChargesReceipt";
import { FaPlus, FaPrint, FaBed, FaEdit, FaTrashAlt, FaCalendarAlt, FaClock, FaReceipt, FaInfoCircle, FaFileInvoiceDollar } from "react-icons/fa";
import { toast } from "react-toastify";

const OngoingCharges = ({ patient, fetchInvoices, registerFetchSummary, prescriptionInvoices, ipInvoices }) => {

  const [showAddChargeComponent, setShowAddChargeComponent] = useState(false);

  const [charges, setCharges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [chargeAmounts, setChargeAmounts] = useState({});
  const CHARGE_TYPES = ["LAB", "MEDICINE", "REGISTRATION", "SCAN", "ROOM", "SERVICES", "SURGERY_CHARGES", "DOCTOR", "CUSTOM_SERVICES", "OTHER", "PACKAGES"];

  const [invoiceSummary, setInvoiceSummary] = useState(null);

  // Edit charge modal state
  const [editingCharge, setEditingCharge] = useState(null);
  const [editForm, setEditForm] = useState({
    chargeName: "",
    unitCost: 0,
    usage: 1,
    amount: 0,
  });

  // Discount & Notes Modal state
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [discountForm, setDiscountForm] = useState({
    discountType: "AMOUNT", // AMOUNT or PERCENTAGE
    value: 0,
    reason: "",
  });

  const [showNotesModal, setShowNotesModal] = useState(false);
  const [customNote, setCustomNote] = useState("");
  const [savedNotes, setSavedNotes] = useState([]);

  // For printing invoices BEGIN
  const [printInvoice, setPrintInvoice] = useState(null);
  const componentRef = useRef();

  useEffect(() => {
    if (printInvoice) {
      setTimeout(() => {
        if (componentRef.current) {
          window.print();
          setPrintInvoice(null);
        }
      }, 100);
    }
  }, [printInvoice]);

  const handlePrintClick = (invoice, e) => {
    e.stopPropagation();
    setPrintInvoice(invoice);
  };

  const openAddChargeComponent = (admissionId) => {
    setShowAddChargeComponent(true);
  };

  const closeAddChargeComponent = () => {
    setShowAddChargeComponent(false);
  };

  const handleAddChargeSubmit = (paymentData) => {
    // optional post submit
  };

  const fetchCharges = useCallback(() => {
    if (!patient?.admissionId) return;
    setLoading(true);
    setError(null);

    axios
      .get(`${ENDPOINTS.GET_CHARGES_BY_ADMISSIONID}/${patient.admissionId}`)
      .then((response) => {
        setCharges(response.data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || "Something went wrong");
        setLoading(false);
      });
  }, [patient?.admissionId, patient]);

  const fetchInvoiceSummary = useCallback(async () => {
    if (!patient?.admissionId) return;
    try {
      const response = await axios.get(`${ENDPOINTS.GET_INVOICE_SUMMARY_FOR_IP}/${patient.admissionId}`);
      setInvoiceSummary(response.data);
    } catch (error) {
      console.error("Error fetching invoice summary:", error);
    }
  }, [patient?.admissionId, patient]);

  const fetchAmount = useCallback(async () => {
    if (!patient?.admissionId) return;
    const amounts = {};

    await Promise.all(
      CHARGE_TYPES.map(async (type) => {
        try {
          const response = await axios.get(`${ENDPOINTS.GET_TOTAL_AMOUNT_BY_ADMISSIONID_AND_CHARGETYPE}`, {
            params: {
              admissionId: patient.admissionId,
              chargeType: type,
            },
          });
          amounts[type] = response.data;
        } catch (err) {
          console.error(`Error fetching for ${type}:`, err);
          amounts[type] = 0;
        }
      })
    );

    setChargeAmounts(amounts);
  }, [patient?.admissionId, patient]);

  useEffect(() => {
    if (registerFetchSummary) {
      registerFetchSummary(fetchInvoiceSummary);
    }
  }, [registerFetchSummary, fetchInvoiceSummary]);

  useEffect(() => {
    if (patient?.admissionId) {
      fetchCharges();
      fetchAmount();
      fetchInvoiceSummary();
    } else {
      setCharges([]);
      setInvoiceSummary(null);
      setChargeAmounts({});
    }
  }, [patient?.admissionId, patient, fetchCharges, fetchAmount, fetchInvoiceSummary]);

  const handleOpenEdit = (charge, e) => {
    e.stopPropagation();
    setEditingCharge(charge);
    const unit = parseFloat(charge.unitCost) || parseFloat(charge.amount) || 0;
    const qty = parseFloat(charge.usage) || 1;
    setEditForm({
      chargeName: charge.chargeName || charge.chargeType || "",
      unitCost: unit,
      usage: qty,
      amount: charge.amount || unit * qty,
    });
  };

  const handleSaveEdit = async () => {
    if (!editingCharge) return;

    const unitCost = parseFloat(editForm.unitCost) || 0;
    const usage = parseFloat(editForm.usage) || 1;
    const calculatedAmount = unitCost * usage;
    const chargeId = editingCharge.inpatientChargeId;

    const payload = {
      chargeName: editForm.chargeName,
      unitCost: unitCost,
      usage: usage,
      amount: calculatedAmount,
    };

    try {
      if (chargeId) {
        await axios.put(`${ENDPOINTS.UPDATE_CHARGE}/${chargeId}`, payload);
      }
      toast.success("Charge details updated successfully!");

      // Refresh list & totals from DB
      fetchCharges();
      fetchInvoiceSummary();
      fetchAmount();
    } catch (err) {
      console.error("Error updating charge in DB:", err);
      // Fallback local update
      setCharges((prev) =>
        prev.map((item) =>
          item === editingCharge || (item.inpatientChargeId && item.inpatientChargeId === chargeId)
            ? { ...item, ...payload }
            : item
        )
      );
      toast.success("Charge details updated!");
    } finally {
      setEditingCharge(null);
    }
  };

  const handleDeleteCharge = async (chargeToDelete, e) => {
    e.stopPropagation();
    if (!window.confirm(`Are you sure you want to remove "${chargeToDelete.chargeName || chargeToDelete.chargeType || 'this charge'}"?`)) {
      return;
    }

    const chargeId = chargeToDelete.inpatientChargeId;

    try {
      if (chargeId) {
        await axios.delete(`${ENDPOINTS.DELETE_CHARGE}/${chargeId}`);
      }
      toast.info("Charge removed from database.");

      // Refresh list & totals from DB
      fetchCharges();
      fetchInvoiceSummary();
      fetchAmount();
    } catch (err) {
      console.error("Error deleting charge from DB:", err);
      // Fallback local remove
      setCharges((prev) =>
        prev.filter((item) => item !== chargeToDelete && item.inpatientChargeId !== chargeId)
      );
      toast.info("Charge removed.");
    }
  };

  const handleApplyDiscount = async () => {
    const val = parseFloat(discountForm.value) || 0;
    if (val <= 0) {
      toast.warning("Please enter a valid discount value greater than 0");
      return;
    }

    const totalCharges = charges.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    
    let discountAmount = 0;
    if (discountForm.discountType === "PERCENTAGE") {
      discountAmount = Math.round((totalCharges * val) / 100);
    } else {
      discountAmount = val;
    }

    try {
      await axios.put(`${ENDPOINTS.APPLY_DISCOUNT_TO_INVOICE}`, null, {
        params: {
          admissionId: patient.admissionId,
          discountAmount: discountAmount,
        },
      });
      toast.success(`Discount of ₹ ${discountAmount} saved to invoice table successfully!`);
      
      // Refresh charges & invoice summary from DB
      fetchCharges();
      fetchInvoiceSummary();
      fetchAmount();
    } catch (err) {
      console.error("Error saving discount to invoice:", err);
      toast.error("Failed to save discount to invoice.");
    } finally {
      setShowDiscountModal(false);
      setDiscountForm({ discountType: "AMOUNT", value: 0, reason: "" });
    }
  };

  const handleAddNote = () => {
    if (!customNote.trim()) {
      toast.warning("Please enter a note before saving");
      return;
    }

    setSavedNotes((prev) => [
      { id: Date.now(), text: customNote.trim(), time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) },
      ...prev,
    ]);

    toast.success("Billing note added successfully!");
    setCustomNote("");
    setShowNotesModal(false);
  };

  const getCategoryIcon = (type) => {
    switch (type) {
      case 'ROOM': return '🛏️';
      case 'SERVICES': return '🩺';
      case 'LAB': return '🧪';
      case 'MEDICINE': return '💊';
      case 'SCAN': return '🩻';
      case 'SURGERY_CHARGES': return '🔪';
      case 'DOCTOR': return '👨‍⚕️';
      default: return '📋';
    }
  };

  return (
    <div className="ongoing-container">
      {/* Top Section Header Toolbar */}
      <div className="ongoing-header-bar">
        <div className="ongoing-header-title">
          <FaFileInvoiceDollar className="ongoing-header-icon" />
          <div>
            <h2>Ongoing IP Charges</h2>
            <p className="ongoing-header-subtitle">Real-time breakdown of inpatient fees and billing items</p>
          </div>
        </div>

        <div className="ongoing-header-actions">
          <button
            className="ongoing-btn primary"
            title="Add New Charge"
            onClick={(e) => {
              e.stopPropagation();
              if (patient && patient.admissionId) {
                openAddChargeComponent(patient.admissionId);
              } else {
                console.warn("Patient or admissionId is missing!", patient);
              }
            }}
          >
            <FaPlus /> Add Charge
          </button>

          <button 
            className="ongoing-btn secondary" 
            title="Print Ongoing Charges Statement"
            onClick={(e) => handlePrintClick(charges, e)}
          >
            <FaPrint /> Print Statement
          </button>
        </div>
      </div>

      {/* Render Receipt on demand */}
      {printInvoice && (
        <div className="print-area">
          <OngoingChargesReceipt ref={componentRef} prescriptionInvoices={prescriptionInvoices} patient={patient} charges={charges} invoiceSummary={invoiceSummary} ipInvoices={ipInvoices} />
        </div>
      )}

      {showAddChargeComponent && (
        <AddChargeComponent
          onClose={closeAddChargeComponent}
          onSubmit={handleAddChargeSubmit}
          refreshInvoices={fetchInvoices}
          refreshCharges={fetchCharges}
          refreshAmounts={fetchAmount}
          refreshInvoiceSummary={fetchInvoiceSummary}
          patient={patient}
        />
      )}

      {/* Room & Admission Information Summary Bar */}
      <div className="room-charge-card">
        <div className="room-card-header">
          <div className="room-title-badge">
            <FaBed /> {patient.roomType || "General Ward"}
          </div>
          <div className="billing-duration-chip">
            <FaClock /> Billing Duration: 1 Day
          </div>
        </div>

        <div className="room-meta-grid">
          <div className="meta-item">
            <span className="meta-label">Bed No:</span>
            <span className="meta-val highlight">{patient.bedNumber || "N/A"}</span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Admission Start:</span>
            <span className="meta-val">{dateTimeFormatter(patient.admissionDate, patient.admissionTime)}</span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Discharge End:</span>
            <span className="meta-val">
              {patient.dischargeDate && patient.dischargeTime ? (
                dateTimeFormatter(patient.dischargeDate, patient.dischargeTime)
              ) : (
                <span className="not-discharged-pill">Active Patient</span>
              )}
            </span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Total Duration:</span>
            <span className="meta-val">
              {calculateDuration(patient.admissionDate, patient.admissionTime, patient.dischargeDate, patient.dischargeTime)}
            </span>
          </div>
        </div>
      </div>

      {/* Unified Master Charges Table */}
      <div className="unified-charges-card">
        <div className="unified-card-header">
          <h3 className="section-subheading">
            <span className="cat-icon">📋</span> All Inpatient Charges ({charges.length})
          </h3>
          <div className="total-charges-badge">
            Total IP Charges: <strong>₹ {charges.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0)}</strong>
          </div>
        </div>

        <div className="table-wrapper">
          <table className="charges-table">
            <thead>
              <tr>
                <th>Category</th>
                <th>Charge Item Name</th>
                <th>Date / Time</th>
                <th>Unit Cost</th>
                <th>Usage</th>
                <th>Amount</th>
                <th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {charges.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: "center", padding: "30px", color: "#64748b" }}>
                    No ongoing charges recorded yet for this admission.
                  </td>
                </tr>
              ) : (
                charges.map((charge, index) => (
                  <tr key={charge.inpatientChargeId || index}>
                    <td>
                      <span className={`cat-pill cat-${(charge.chargeType || 'other').toLowerCase()}`}>
                        {getCategoryIcon(charge.chargeType)} {(charge.chargeType || 'OTHER').replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td><strong>{charge.chargeName || charge.chargeType}</strong></td>
                    <td>{charge.chargeDate ? dateTimeFormatter2(charge.chargeDate) : dateTimeFormatter(patient.admissionDate, patient.admissionTime)}</td>
                    <td>₹ {charge.unitCost || charge.amount || 0}</td>
                    <td>{charge.usage || 1}</td>
                    <td className="charge-amount-cell">₹ {charge.amount}</td>
                    <td style={{ textAlign: "right" }}>
                      <button 
                        className="table-action-btn edit" 
                        title="Edit Charge"
                        onClick={(e) => handleOpenEdit(charge, e)}
                      >
                        <FaEdit /> Edit
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="category-total-banner">
          <span>Accumulated IP Charges Subtotal:</span>
          <strong>₹ {charges.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0)}</strong>
        </div>
      </div>

      {/* Edit Charge Modal */}
      {editingCharge && (
        <div className="modal-backdrop" onClick={() => setEditingCharge(null)}>
          <div className="edit-charge-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3><FaEdit /> Edit Inpatient Charge</h3>
              <button className="modal-close-btn" onClick={() => setEditingCharge(null)}>✕</button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label className="input-label">Charge Item Name</label>
                <input
                  type="text"
                  className="modal-input"
                  value={editForm.chargeName}
                  onChange={(e) => setEditForm({ ...editForm, chargeName: e.target.value })}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="input-label">Unit Cost (₹)</label>
                  <input
                    type="number"
                    className="modal-input"
                    value={editForm.unitCost}
                    onChange={(e) => {
                      const cost = parseFloat(e.target.value) || 0;
                      setEditForm({
                        ...editForm,
                        unitCost: cost,
                        amount: cost * (parseFloat(editForm.usage) || 1),
                      });
                    }}
                  />
                </div>

                <div className="form-group">
                  <label className="input-label">Usage / Qty</label>
                  <input
                    type="number"
                    className="modal-input"
                    value={editForm.usage}
                    onChange={(e) => {
                      const usage = parseFloat(e.target.value) || 1;
                      setEditForm({
                        ...editForm,
                        usage: usage,
                        amount: (parseFloat(editForm.unitCost) || 0) * usage,
                      });
                    }}
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="input-label">Calculated Amount (₹)</label>
                <input
                  type="number"
                  className="modal-input bold-amount"
                  value={editForm.amount}
                  readOnly
                />
              </div>
            </div>

            <div className="modal-footer">
              <button className="ongoing-btn secondary" onClick={() => setEditingCharge(null)}>Cancel</button>
              <button className="ongoing-btn primary" onClick={handleSaveEdit}>Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* Billing Summary Footer Dashboard */}
      <div className="ongoing-footer-dashboard">
        {/* Left Side: Discounts & Notes */}
        <div className="footer-notes-panel">
          <h4 className="panel-title">Billing Actions & Notes</h4>
          <div className="action-buttons-group">
            <button className="ongoing-btn outline" onClick={() => setShowDiscountModal(true)}>
              <FaPlus /> Add Discount
            </button>
            <button className="ongoing-btn outline" onClick={() => setShowNotesModal(true)}>
              <FaReceipt /> Add Notes
            </button>
          </div>

          <div className="notes-box">
            <div className="notes-box-header">
              <FaInfoCircle /> Important Billing Note
            </div>
            <p>
              Ongoing charges accumulate in real-time. Final clearance invoices will be compiled automatically upon discharge processing.
            </p>
            {savedNotes.length > 0 && (
              <div style={{ marginTop: "10px", paddingTop: "8px", borderTop: "1px dashed #cbd5e1" }}>
                <strong>Saved Notes:</strong>
                {savedNotes.map((note) => (
                  <div key={note.id} style={{ fontSize: "12px", color: "#334155", marginTop: "4px" }}>
                    • [{note.time}] {note.text}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Side: Grand Total Summary Table */}
        {invoiceSummary ? (
          <div className="grand-total-card">
            <h4 className="card-title">Executive Invoice Summary</h4>
            <div className="summary-rows">
              <div className="summary-row tooltip-group">
                <span className="row-label">
                  Grand Total <FaInfoCircle className="info-icon" />
                  <span className="tooltip-text">
                    Total Charge (IP Charges) + Lab paid amount + Pharmacy paid amount.
                  </span>
                </span>
                <span className="row-val grand">₹ {invoiceSummary.grandTotal}</span>
              </div>

              <div className="summary-row tooltip-group">
                <span className="row-label">
                  Total IP Charge <FaInfoCircle className="info-icon" />
                  <span className="tooltip-text">Includes all Inpatient charges only</span>
                </span>
                <span className="row-val">₹ {invoiceSummary.totalCharge}</span>
              </div>

              <div className="summary-row tooltip-group">
                <span className="row-label">
                  IP Paid Amount <FaInfoCircle className="info-icon" />
                  <span className="tooltip-text">Total paid amount for IP charges</span>
                </span>
                <span className="row-val success">₹ {invoiceSummary.ipPaidAmount}</span>
              </div>

              <div className="summary-row tooltip-group">
                <span className="row-label">
                  Pharmacy Paid Amount <FaInfoCircle className="info-icon" />
                  <span className="tooltip-text">Total paid amount for Pharmacy invoices</span>
                </span>
                <span className="row-val success">₹ {invoiceSummary.pharmacyPaidAmount}</span>
              </div>

              {invoiceSummary.discount > 0 && (
                <div className="summary-row tooltip-group">
                  <span className="row-label">
                    Discount Applied <FaInfoCircle className="info-icon" />
                    <span className="tooltip-text">Concession amount saved in invoice table</span>
                  </span>
                  <span className="row-val success" style={{ color: '#16a34a', fontWeight: '700' }}>
                    - ₹ {invoiceSummary.discount}
                  </span>
                </div>
              )}

              <div className="summary-divider" />

              <div className="summary-row balance-row">
                <span className="balance-label">Total Balance Due</span>
                <span className={`balance-val ${invoiceSummary.totalBalance > 0 ? "due" : "paid"}`}>
                  ₹ {invoiceSummary.totalBalance}
                </span>
              </div>
            </div>
          </div>
        ) : (
          <div className="summary-loading">
            <div className="spinner-border text-primary" role="status" />
            <p>Loading Invoice Summary...</p>
          </div>
        )}
      </div>

      {/* Add Discount Modal */}
      {showDiscountModal && (
        <div className="modal-backdrop" onClick={() => setShowDiscountModal(false)}>
          <div className="edit-charge-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3><FaPlus /> Apply Inpatient Discount</h3>
              <button className="modal-close-btn" onClick={() => setShowDiscountModal(false)}>✕</button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label className="input-label">Discount Type</label>
                <select
                  className="modal-input"
                  value={discountForm.discountType}
                  onChange={(e) => setDiscountForm({ ...discountForm, discountType: e.target.value })}
                >
                  <option value="AMOUNT">Fixed Amount (₹)</option>
                  <option value="PERCENTAGE">Percentage (%)</option>
                </select>
              </div>

              <div className="form-group">
                <label className="input-label">
                  Discount Value {discountForm.discountType === "AMOUNT" ? "(₹)" : "(%)"}
                </label>
                <input
                  type="number"
                  className="modal-input"
                  placeholder="e.g. 500 or 10"
                  value={discountForm.value || ""}
                  onChange={(e) => setDiscountForm({ ...discountForm, value: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label className="input-label">Reason / Concession Note</label>
                <input
                  type="text"
                  className="modal-input"
                  placeholder="e.g. Senior Citizen Waiver / Staff Courtesy"
                  value={discountForm.reason}
                  onChange={(e) => setDiscountForm({ ...discountForm, reason: e.target.value })}
                />
              </div>
            </div>

            <div className="modal-footer">
              <button className="ongoing-btn secondary" onClick={() => setShowDiscountModal(false)}>Cancel</button>
              <button className="ongoing-btn primary" onClick={handleApplyDiscount}>Apply Discount</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Notes Modal */}
      {showNotesModal && (
        <div className="modal-backdrop" onClick={() => setShowNotesModal(false)}>
          <div className="edit-charge-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3><FaReceipt /> Add Administrative Note</h3>
              <button className="modal-close-btn" onClick={() => setShowNotesModal(false)}>✕</button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label className="input-label">Billing Note / Reminders</label>
                <textarea
                  className="modal-input"
                  rows="4"
                  placeholder="Enter billing instructions, insurance authorizations, or special notes..."
                  value={customNote}
                  onChange={(e) => setCustomNote(e.target.value)}
                />
              </div>
            </div>

            <div className="modal-footer">
              <button className="ongoing-btn secondary" onClick={() => setShowNotesModal(false)}>Cancel</button>
              <button className="ongoing-btn primary" onClick={handleAddNote}>Save Note</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OngoingCharges;
